import { Button, Input, Modal, Textarea } from '@orobix/orobix-fe-components'
import { useMutation } from '@tanstack/react-query'
import { Formik, FormikErrors } from 'formik'
import { useTranslation } from 'react-i18next'

import { ErrorText } from '../../components/ErrorText/ErrorText'
import { TrainingTest } from '../../store/store.types'
import { update } from '../../utils/api'

type FormValues = {
  name: string
  notes: string
}

type TrainingTestUpdateProps = {
  onClose: () => void
  onSuccess: () => void
  trainingTestId: number
  initialValues?: FormValues
}

const TrainingTestUpdate = ({
  onClose,
  onSuccess,
  trainingTestId,
  initialValues = {
    name: '',
    notes: '',
  },
}: TrainingTestUpdateProps) => {
  const { t } = useTranslation()

  const { mutate, isLoading, isError } = useMutation(
    (values: FormValues) =>
      update<TrainingTest>(`/training_tests/${trainingTestId}/`, values),
    { onSuccess }
  )
  return (
    <Formik
      initialValues={initialValues}
      validate={(values) => {
        const errors: FormikErrors<{ [field: string]: string }> = {}

        if (!values.name) {
          errors.name = t('This field is required')
        }

        return errors
      }}
      onSubmit={(values, { setSubmitting }) => {
        mutate(values)
        setSubmitting(false)
      }}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleSubmit,
        isSubmitting,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <Modal
            size="md"
            onCloseRequest={onClose}
            title={t('Training test details')}
            footer={
              <Button
                label={t('Edit')}
                type="action"
                disabled={isSubmitting}
                loading={isLoading}
              />
            }
          >
            <Input
              name="name"
              id="name"
              label={t('Name')}
              type="text"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setFieldValue('name', e.target.value)
              }
              onBlur={handleBlur}
              value={values.name}
              error={!!(errors.name && touched.name)}
              errorMessage={errors.name}
              disabled={isSubmitting}
            />

            <Textarea
              name="notes"
              id="notes"
              label={t('Notes')}
              type="text"
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setFieldValue('notes', e.target.value)
              }
              onBlur={handleBlur}
              value={values.notes}
              error={!!(errors.notes && touched.notes)}
              errorMessage={errors.notes}
              disabled={isSubmitting}
            />

            {isError && <ErrorText />}
          </Modal>
        </form>
      )}
    </Formik>
  )
}

export default TrainingTestUpdate
